'use client';

import { Dropdown, LoadingIndicator, TextField, TextPassage } from '@petsmart-ui/sparky';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useField } from 'formik';
import { clsx, useOnClickOutside } from '@libs/common-next';
import type { SuggestedAddressInterface, SuggestionResponseInterface } from './AddressForm';

const componentClassname = 'street-address-line';

export interface StreetAddressLineInterface {
    dropdownIsOpen: boolean;
    setDropdownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    suggestedAddresses: SuggestedAddressInterface[] | null;
    handleSuggestedAddressClick: (address: SuggestedAddressInterface) => void;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    checkAddressSuggestions: (value: string) => Promise<SuggestionResponseInterface | null>;
    setSuggestedAddresses: React.Dispatch<React.SetStateAction<SuggestedAddressInterface[] | null>>;
}

export function StreetAddressLine({
    dropdownIsOpen,
    suggestedAddresses,
    handleSuggestedAddressClick,
    loading,
    setLoading,
    checkAddressSuggestions,
    setDropdownIsOpen,
    setSuggestedAddresses,
}: StreetAddressLineInterface) {
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const clearSuggestions = () => {
        setDropdownIsOpen(false);
    };

    useOnClickOutside(containerRef, clearSuggestions);

    const [{ value, onChange, onBlur }, { touched, error }] = useField('address.streetAddress');

    const formatMatchedText = (
        text: SuggestedAddressInterface['suggestion'],
        matches: SuggestedAddressInterface['matched']
    ) => {
        const formattedText = [];
        let currentIndex = 0;
        matches.sort(([startA], [startB]) => startA - startB);
        matches.forEach(([start, end]) => {
            if (currentIndex < start) {
                formattedText.push(text.slice(currentIndex, start));
            }
            formattedText.push(<strong key={start}>{text.slice(start, end)}</strong>);
            currentIndex = end;
        });
        if (currentIndex < text.length) {
            formattedText.push(text.slice(currentIndex));
        }

        return formattedText.length > 0 ? formattedText : text;
    };

    const handleStreetInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: inputValue } = e.target;

        setSuggestedAddresses(null);

        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }

        if (inputValue) {
            timeoutIdRef.current = setTimeout(() => {
                setLoading(true);
                checkAddressSuggestions(inputValue);
            }, 500);
            setDropdownIsOpen(true);
        } else {
            setDropdownIsOpen(false);
        }
    };

    useEffect(
        () => () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        },
        []
    );

    return (
        <div className={componentClassname} ref={containerRef}>
            <TextField
                type="text"
                data-testid="streetAddressInput"
                data-cs-mask
                label="Street address"
                name="address.streetAddress"
                isError={touched && !!error}
                fieldNote={touched && error && error[0]}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(e);
                    handleStreetInput(e);
                }}
                onFocus={() => {
                    setDropdownIsOpen(true);
                }}
                onBlur={onBlur}
            />
            {dropdownIsOpen && suggestedAddresses && value.length > 0 && (
                <Dropdown className={`${componentClassname}__dropdown`} data-cs-mask>
                    {suggestedAddresses.length > 0
                        ? suggestedAddresses.map(address => (
                              <TextPassage
                                  key={address.format}
                                  className={`${componentClassname}__fpo`}
                                  size="lg"
                                  onClick={() => handleSuggestedAddressClick(address)}
                                  data-testid="address-fpo"
                              >
                                  {formatMatchedText(address.suggestion, address.matched)}
                              </TextPassage>
                          ))
                        : !loading && (
                              <TextPassage key="no-results-found" className={`${componentClassname}__fpo`}>
                                  No results found.
                              </TextPassage>
                          )}
                </Dropdown>
            )}{' '}
            {loading && <LoadingIndicator iconSize="lg" className={clsx(`${componentClassname}__loader`)} />}
        </div>
    );
}
